"use client"

import { t } from "@/languages/i18n"
import { LiveNewsItemProps } from "@/types/types"
import { CaretDownOutlined, CaretUpOutlined, DeleteOutlined } from "@ant-design/icons"
import { Button, DatePicker, Form, Input, TimePicker, Typography } from "antd"
import dayjs from "dayjs"
import dynamic from "next/dynamic"
import React, { useState } from "react"

const CKEditorComponent = dynamic(() => import("../../../../lib/config/lib/Ckeditor"), { ssr: false })

const { Title } = Typography

const LiveNewsItem: React.FC<LiveNewsItemProps> = ({ index, data, onChange, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [quillValue, setQuillValue] = useState(data?.detail || "")

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.({
      ...data,
      title: e.target.value,
      date: data?.date || "",
      detail: quillValue,
    })
  }

  const handleDateTimeChange = (date: dayjs.Dayjs | null, timeString: string) => {
    const dateTime = date ? date.format("YYYY-MM-DD") + "T" + timeString + ":00.000Z" : ""
    onChange?.({
      ...data,
      title: data?.title || "",
      date: dateTime,
      detail: quillValue,
    })
  }

  const handleDetailsChange = (content: string) => {
    setQuillValue(content)
    onChange?.({
      ...data,
      title: data?.title || "",
      date: data?.date || "",
      detail: content,
    })
  }

  return (
    <div className="border border-gray-300 rounded-[10px]">
      <div className="flex justify-between items-center px-4 py-2 bg-[#008746] text-white rounded-t-[10px]">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center text-white border-white"
            icon={isExpanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
            type="text"
          />
          <Title level={4} className="!text-white !m-0">
            {t("Live News Item")} ({index + 1})
          </Title>
        </div>
        <Button
          onClick={onDelete}
          className="text-white border-white hover:bg-white hover:text-green-500"
          icon={<DeleteOutlined />}
          type="text"
        />
      </div>

      {isExpanded && (
        <div className="p-4">
          <Form.Item label={<span className="text-base font-medium">{t("Title")}*</span>} required className="">
            <Input
              placeholder={t("Enter title")}
              value={data?.title || ""}
              onChange={handleTitleChange}
              className="custom-input"
            />
          </Form.Item>

          <Form.Item label={<span className="text-base font-medium">{t("Date & Time")}*</span>} required className="">
            <div className="flex gap-4">
              <DatePicker
                placeholder={t("Select date")}
                className="flex-1"
                value={data?.date ? dayjs(data.date.split("T")[0]) : null}
                onChange={date => {
                  const timeString = data?.date
                    ? data.date.split("T")[1]?.split(":")[0] + ":" + data.date.split("T")[1]?.split(":")[1]
                    : "00:00"
                  handleDateTimeChange(date, timeString)
                }}
              />
              <TimePicker
                placeholder={t("Select time")}
                className="flex-1"
                value={data?.date ? dayjs(data.date.split("T")[1]?.split(".")[0], "HH:mm:ss") : null}
                onChange={time => {
                  const dateString = data?.date?.split("T")[0] || dayjs().format("YYYY-MM-DD")
                  const timeString = time ? time.format("HH:mm") : "00:00"
                  handleDateTimeChange(dayjs(dateString), timeString)
                }}
              />
            </div>
          </Form.Item>

          <Form.Item label={<span className="text-base font-medium">{t("Details")}*</span>} required className="">
            <div
              style={{
                height: "300px",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <CKEditorComponent value={quillValue} onChange={handleDetailsChange} />
            </div>
          </Form.Item>
        </div>
      )}
    </div>
  )
}

export default LiveNewsItem
